import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  CheckCircle,
  Error,
  Warning,
  NetworkCheck,
  Refresh,
} from "@mui/icons-material";

const ConnectionDiagnostics = () => {
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState([]);

  const runDiagnostics = async () => {
    setTesting(true);
    setResults([]);

    const testResults = [];

    // Test 1: Backend Server Connectivity
    try {
      console.log("🧪 Testing backend server...");
      const serverUrl =
        process.env.REACT_APP_SERVER_URL || "http://localhost:5000";
      const response = await fetch(serverUrl, {
        method: "GET",
        cache: "no-cache",
        headers: { "Content-Type": "application/json" },
      });

      testResults.push({
        name: "Backend Server",
        url: serverUrl,
        status: response.ok ? "success" : "error",
        message: response.ok
          ? `Connected (${response.status})`
          : `Failed (${response.status} ${response.statusText})`,
      });
    } catch (error) {
      testResults.push({
        name: "Backend Server",
        url: process.env.REACT_APP_SERVER_URL || "http://localhost:5000",
        status: "error",
        message: `Connection failed: ${error.message}`,
      });
    }

    // Test 2: API Base Endpoint
    try {
      console.log("🧪 Testing API base endpoint...");
      const apiUrl = `${
        process.env.REACT_APP_SERVER_URL || "http://localhost:5000"
      }/api/v1`;
      const response = await fetch(apiUrl);
      const data = await response.json();

      testResults.push({
        name: "API Base Endpoint",
        url: apiUrl,
        status: response.ok ? "success" : "error",
        message: response.ok
          ? `API responding: ${data.message}`
          : `API failed (${response.status})`,
      });
    } catch (error) {
      testResults.push({
        name: "API Base Endpoint",
        url: `${
          process.env.REACT_APP_SERVER_URL || "http://localhost:5000"
        }/api/v1`,
        status: "error",
        message: `API connection failed: ${error.message}`,
      });
    }

    // Test 3: Authentication Endpoint
    try {
      console.log("🧪 Testing auth endpoint...");
      const authUrl = `${
        process.env.REACT_APP_SERVER_URL || "http://localhost:5000"
      }/api/v1/auth`;
      const response = await fetch(authUrl);

      testResults.push({
        name: "Authentication Endpoint",
        url: authUrl,
        status: response.ok ? "success" : "warning",
        message: response.ok
          ? "Auth endpoint accessible"
          : `Auth issue (${response.status})`,
      });
    } catch (error) {
      testResults.push({
        name: "Authentication Endpoint",
        url: `${
          process.env.REACT_APP_SERVER_URL || "http://localhost:5000"
        }/api/v1/auth`,
        status: "error",
        message: `Auth endpoint failed: ${error.message}`,
      });
    }

    // Test 4: University ID Lookup
    try {
      console.log("🧪 Testing University ID lookup...");
      const universityUrl = `${
        process.env.REACT_APP_SERVER_URL || "http://localhost:5000"
      }/api/v1/auth/university/diagnostic-test`;
      const response = await fetch(universityUrl);

      testResults.push({
        name: "University ID Lookup",
        url: universityUrl,
        status: response.ok ? "success" : "warning",
        message: response.ok
          ? "University lookup working"
          : `Lookup issue (${response.status})`,
      });
    } catch (error) {
      testResults.push({
        name: "University ID Lookup",
        url: `${
          process.env.REACT_APP_SERVER_URL || "http://localhost:5000"
        }/api/v1/auth/university/diagnostic-test`,
        status: "error",
        message: `Lookup failed: ${error.message}`,
      });
    }

    setResults(testResults);
    setTesting(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle color="success" />;
      case "warning":
        return <Warning color="warning" />;
      case "error":
        return <Error color="error" />;
      default:
        return <NetworkCheck />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "success";
      case "warning":
        return "warning";
      case "error":
        return "error";
      default:
        return "default";
    }
  };

  const hasErrors = results.some((r) => r.status === "error");
  const hasWarnings = results.some((r) => r.status === "warning");

  return (
    <Card sx={{ mt: 2, mb: 2 }}>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <NetworkCheck />
            Connection Diagnostics
          </Typography>
          <Button
            variant="contained"
            onClick={runDiagnostics}
            disabled={testing}
            startIcon={testing ? <CircularProgress size={20} /> : <Refresh />}
          >
            {testing ? "Testing..." : "Run Tests"}
          </Button>
        </Box>

        {results.length > 0 && (
          <>
            {hasErrors && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Connection issues detected! Your backend server may not be
                running on port 5000.
              </Alert>
            )}
            {!hasErrors && hasWarnings && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Some endpoints have minor issues but basic connectivity is
                working.
              </Alert>
            )}
            {!hasErrors && !hasWarnings && (
              <Alert severity="success" sx={{ mb: 2 }}>
                All connection tests passed! The server appears to be working
                correctly.
              </Alert>
            )}

            <List>
              {results.map((result, index) => (
                <ListItem key={index}>
                  <ListItemIcon>{getStatusIcon(result.status)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        {result.name}
                        <Chip
                          label={result.status.toUpperCase()}
                          size="small"
                          color={getStatusColor(result.status)}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {result.message}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {result.url}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {results.length === 0 && !testing && (
          <Alert severity="info">
            Click "Run Tests" to check your backend server connectivity and
            diagnose authentication issues.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default ConnectionDiagnostics;
